import type { ReactNode } from 'react';
import { TopNav } from './TopNav';

/**
 * PageShell — shell visual único de TODAS as abas (extraído da Semanal).
 *
 * Garante uniformidade: TopNav + sub-barra sticky branca (abaixo do TopNav) +
 * container central de largura padrão. Cada página passa só o seu conteúdo.
 *
 *   <PageShell titulo={<h1 className="...">🗓 Mensal</h1>} acoes={<button/>}>
 *     ...conteúdo...
 *   </PageShell>
 *
 * Casos especiais:
 *  - `subbarra`         — substitui a linha título/ações por conteúdo próprio (ex.: Semanal: ‹ Hoje › + busca + termômetro).
 *  - `abaixoDaSubbarra` — segunda linha dentro da faixa sticky (ex.: filtros da Semanal).
 *  - `className`        — classes extras no root (ex.: privacy/sla da Semanal).
 *  - `semMain`          — não envolve em <main> com padding (páginas full-bleed, ex.: iframe Clientes).
 *  - `largura`          — largura do container (default uniforme: max-w-screen-2xl).
 */
const LARGURA_PADRAO = 'max-w-screen-2xl';

interface Props {
  titulo?: ReactNode;
  acoes?: ReactNode;
  subbarra?: ReactNode;
  abaixoDaSubbarra?: ReactNode;
  className?: string;
  largura?: string;
  semMain?: boolean;
  children: ReactNode;
}

export function PageShell({
  titulo,
  acoes,
  subbarra,
  abaixoDaSubbarra,
  className = '',
  largura = LARGURA_PADRAO,
  semMain = false,
  children,
}: Props) {
  const temSubbarra = !!(subbarra || titulo || acoes);
  return (
    <div className={className}>
      <TopNav />

      {temSubbarra && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-[60px] z-20">
          <div className={`${largura} mx-auto px-4 py-2 flex items-center justify-between gap-4`}>
            {subbarra ?? (
              <>
                <div className="flex items-center gap-2 min-w-0">{titulo}</div>
                <div className="flex items-center gap-2 shrink-0">{acoes}</div>
              </>
            )}
          </div>
          {abaixoDaSubbarra && (
            <div className={`${largura} mx-auto px-4 pb-2`}>{abaixoDaSubbarra}</div>
          )}
        </div>
      )}

      {semMain ? children : <main className={`${largura} mx-auto p-4`}>{children}</main>}
    </div>
  );
}
